// Where the window sat on each history entry of this page load (#4602), keyed
// by React Router's location.key. In memory only: a back step inside the app
// restores from here; a fresh load starts at the top, as it always has.
//
// Each entry keeps the raw offset AND an anchor: the nearest element with an
// id around the middle of the screen (on the landing page, the section the
// visitor was reading) and how far from the top of the window it sat. A page
// coming back does not always come back at its final height: on a phone the
// landing page's hero grows ~240px a moment after it remounts, which would
// push "Look up a college" down by that much under a restored offset. The
// anchor lets the restore follow the section instead of the number.
//
// Bounded, oldest first, so a long session never grows it without end.
const MAX_ENTRIES = 100;
const positions = new Map();

export function rememberScroll(key, y, anchor = null) {
    if (!key || !Number.isFinite(y)) return;
    const prev = positions.get(key);
    positions.delete(key);
    positions.set(key, {
        y: Math.max(0, Math.round(y)),
        // A scroll with no readable anchor keeps the last one only when the
        // offset did not move; otherwise it would point at the wrong place.
        anchor: anchor || (prev && prev.y === Math.round(y) ? prev.anchor : null),
    });
    if (positions.size > MAX_ENTRIES) positions.delete(positions.keys().next().value);
}

// { y, anchor } or null when this entry was never scrolled (or was dropped):
// the caller then starts at the top.
export function recallScroll(key) {
    return positions.has(key) ? positions.get(key) : null;
}

export function forgetScrollPositions() {
    positions.clear();
}

// The element with an id that holds the point 40% down the screen, and its
// distance from the top of the window. Null where there is none (or where
// layout does not exist, as in jsdom).
export function anchorAt(doc = document, win = window) {
    if (!doc || typeof doc.elementFromPoint !== 'function') return null;
    const hit = doc.elementFromPoint(Math.round(win.innerWidth / 2), Math.round(win.innerHeight * 0.4));
    const el = hit && typeof hit.closest === 'function' ? hit.closest('[id]') : null;
    if (!el || el === doc.body || el === doc.documentElement || el.id === 'root') return null;
    return { id: el.id, top: Math.round(el.getBoundingClientRect().top) };
}

// Where to put the window so the saved entry shows what it showed: the anchor
// back at its old distance from the top when it is on the page, else the raw
// offset.
export function restoreTarget(saved, doc = document, win = window) {
    if (!saved) return 0;
    const el = saved.anchor && doc.getElementById(saved.anchor.id);
    if (!el) return saved.y;
    return Math.max(0, Math.round(win.scrollY + el.getBoundingClientRect().top - saved.anchor.top));
}
